import ResponsiveImage from "../../common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../../../lib/language";
import React from "react";
import { SectionHeader, CheckboxField } from "./fields";
import {
  TITLE_MIN,
  TITLE_MAX,
  SHORT_DESC_MIN,
  SHORT_DESC_MAX,
  FULL_DESC_MIN,
  FULL_DESC_MAX,
  HIGHLIGHT_MIN,
  HIGHLIGHT_MAX,
  HIGHLIGHT_COUNT,
  VISIT_LOCATIONS_MIN,
  PHOTO_MIN_COUNT,
  REFUND_POLICY_OPTIONS,
  REFUND_POLICY_UNITS,
  STEPS,
} from "./wizardConstants";

// Jump by step key, not position: positions shift whenever a wizard step is added.
export const stepIndex = (key) => STEPS.findIndex((s) => s.key === key);

/* ---------- Helpers ---------- */
const PRICE_MAX = 9999999.99;

const between = (s, min, max) => {
  const len = (s || "").trim().length;
  return len >= min && len <= max;
};

const toLocalDateString = (date) => date.toLocaleDateString("en-CA");

const addYears = (base, years) => {
  const next = new Date(base);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const scheduleDatesAreValid = (opt) => {
  const todayStr = toLocalDateString(new Date());
  const startMax = toLocalDateString(addYears(new Date(), 1));
  const endMax = toLocalDateString(addYears(new Date(), 10));

  // A past start date is allowed: saved and cloned products keep their original date.
  if (opt.schedule_start_date && opt.schedule_start_date > startMax) return false;
  if (opt.has_schedule_end_date && !opt.schedule_end_date) return false;
  if (
    opt.schedule_end_date &&
    opt.schedule_start_date &&
    opt.schedule_end_date < opt.schedule_start_date
  ) {
    return false;
  }
  if (opt.schedule_end_date && opt.schedule_end_date > endMax) return false;
  return true;
};

const priceIsValid = (value) => {
  const n = Number(value);
  return n > 0 && n <= PRICE_MAX;
};

const effectiveTierTo = (tiers, index, capacityMax) => {
  const raw = tiers?.[index]?.to;
  if (raw !== "" && raw !== null && raw !== undefined) return raw;

  const capMax = Number(capacityMax);
  if (index === (tiers || []).length - 1 && Number.isFinite(capMax) && capMax > 0) {
    return capMax;
  }

  return raw;
};

const tiersAreComplete = (tiers, capacityMax) =>
  Array.isArray(tiers) &&
  tiers.length > 0 &&
  tiers.every((tier, index) => {
    const to = Number(effectiveTierTo(tiers, index, capacityMax));
    return to > 0 && priceIsValid(tier?.price);
  });

/* ---------- Required-field rules ---------- */
const RULES = {
  basics: [
    { key: "title", get label() { return tr("Title (10–60 chars)"); }, check: (d) => between(d.title, TITLE_MIN, TITLE_MAX) },
    { key: "short_description", get label() { return tr("Short description (40–200 chars)"); }, check: (d) => between(d.short_description, SHORT_DESC_MIN, SHORT_DESC_MAX) },
    { key: "full_description", get label() { return tr("Full description (500–3,000 chars)"); }, check: (d) => between(d.full_description, FULL_DESC_MIN, FULL_DESC_MAX) },
    {
      key: "highlights",
      label: `${HIGHLIGHT_COUNT} highlights (${HIGHLIGHT_MIN}–${HIGHLIGHT_MAX} chars each)`,
      check: (d) =>
        Array.isArray(d.highlights) &&
        d.highlights.length === HIGHLIGHT_COUNT &&
        d.highlights.every((h) => between(h, HIGHLIGHT_MIN, HIGHLIGHT_MAX)),
    },
    {
      key: "visit_locations",
      label: `Locations (at least ${VISIT_LOCATIONS_MIN})`,
      check: (d) => (d.visit_locations || []).length >= VISIT_LOCATIONS_MIN,
    },
    { key: "experience_leader", get label() { return tr("Who leads the experience"); }, check: (d) => !!d.experience_leader },
    {
      key: "refund_policy_type",
      get label() { return tr("Refund policy"); },
      check: (d) => {
        const type = d.refund_policy_type || "standard_24_hours";
        if (!REFUND_POLICY_OPTIONS.some((option) => option.value === type)) return false;
        if (type !== "custom") return true;
        return /^\d{1,2}$/.test(String(d.custom_refund_policy_value || "")) &&
          Number(d.custom_refund_policy_value) >= 1 &&
          REFUND_POLICY_UNITS.includes(d.custom_refund_policy_unit);
      },
    },
  ],
  visuals: [
    {
      key: "photos",
      label: `Photos (at least ${PHOTO_MIN_COUNT})`,
      check: (d) => (d.photos || []).length >= PHOTO_MIN_COUNT,
    },
    {
      key: "copyright_confirmed",
      get label() { return tr("Copyright & rights confirmed"); },
      check: (d) => !!d.copyright_confirmed,
    },
  ],
  option: [
    {
      key: "options",
      get label() { return tr("At least one valid option"); },
      check: (d) => Array.isArray(d.options) && d.options.length >= 1 && d.options.every(isOptionValid),
    },
  ],
};

export function isOptionValid(opt) {
  if (!opt) return false;
  const nameLen = (opt.name || "").trim().length;
  if (nameLen < 10 || nameLen > 60) return false;
  if (!(opt.languages || []).length) return false;
  if (!opt.privacy_type) return false;
  // Duration vs Validity branch
  if (!opt.duration_or_validity) return false;
  if (opt.duration_or_validity === "duration") {
    if (!opt.duration_unit || !(Number(opt.duration_value) > 0)) return false;
  } else if (opt.duration_or_validity === "validity") {
    if (!opt.validity_type) return false;
    if (
      opt.validity_type === "from_booking" ||
      opt.validity_type === "from_activation"
    ) {
      if (!opt.duration_unit || !(Number(opt.duration_value) > 0)) return false;
    }
  } else {
    return false;
  }
  if (!opt.meeting_point_type) return false;
  if (
    opt.meeting_point_type === "set_meeting_point" &&
    !(opt.meeting_point_location || "").trim()
  )
    return false;
  if (
    opt.meeting_point_type === "pickup" &&
    !(opt.pickup_area_description || "").trim()
  )
    return false;
  if (opt.meeting_point_type === "pickup" && !opt.pickup_transport_mode)
    return false;
  if (!opt.availability_mode) return false;
  if ((opt.schedule_slots || []).length === 0) return false;
  if (!scheduleDatesAreValid(opt)) return false;

  // ---- Pricing ----
  if (!opt.pricing_basis) return false;
  if (opt.pricing_basis === "per_person") {
    if (!opt.pricing_mode) return false;
    const cMin = Number(opt.capacity_min);
    const cMax = Number(opt.capacity_max);
    if (!(cMin >= 1)) return false;
    if (!(cMax >= cMin)) return false;
    if (opt.pricing_mode === "flat") {
      const tiers = opt.flat_tiers || [];
      if (!tiersAreComplete(tiers, opt.capacity_max)) return false;
    } else if (opt.pricing_mode === "per_category") {
      const cats = opt.categories || [];
      const hasAdult = cats.find(
        (c) =>
          c.key === "adult" &&
          tiersAreComplete(c.tiers, opt.capacity_max),
      );
      const hasChild = cats.find(
        (c) =>
          c.key === "child" &&
          tiersAreComplete(c.tiers, opt.capacity_max),
      );
      if (!hasAdult || !hasChild) return false;
    } else {
      return false;
    }
  } else if (opt.pricing_basis === "per_group") {
    const sizes = (opt.group_sizes || []).filter((s) => Number(s.to) > 0);
    if (sizes.length === 0) return false;
    const missingPrice = sizes.find(
      (s) => !priceIsValid((opt.group_prices || {})[String(Number(s.to))]),
    );
    if (missingPrice) return false;
  } else {
    return false;
  }
  return true;
}

/* ---------- Compact summary cards (read-only previews) ---------- */
const SummaryCard = ({
  title,
  onEdit,
  children,
  testid
}) => {
  usePartnerTranslation();
  return <div data-testid={testid} className="rounded-lg border border-[#F0F2F5] bg-white p-4">
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-xs font-extrabold uppercase tracking-wider text-[#0F1115]">
        {title}
      </h3>
      <button type="button" onClick={onEdit} data-testid={testid ? `${testid}-edit` : undefined} className="text-xs font-semibold text-[#2476FF] hover:underline">{tr("Edit")}</button>
    </div>
    <div className="mt-3 space-y-2 text-sm text-[#0F1115]">{children}</div>
  </div>;
};

const Row = ({
  label,
  children
}) => {
  usePartnerTranslation();
  return <div className="flex flex-wrap items-baseline gap-x-3">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#9BA1AB]">
      {label}
    </span>
    <span className="text-sm text-[#0F1115]">{children}</span>
  </div>;
};

const ListLines = ({
  items
}) => {
  usePartnerTranslation();
  return items && items.length ? <ul className="space-y-1 text-sm text-[#0F1115]">
      {items.map((it, i) => <li key={i}>· {it}</li>)}
    </ul> : <span className="text-sm italic text-[#9BA1AB]">—</span>;
};

const expandInclusions = (tags = [], otherText = "") => {
  const fromTags = (tags || []).filter((t) => t !== "Other");
  const fromOther =
    otherText
      ?.split("\n")
      .map((s) => s.trim())
      .filter(Boolean) || [];
  return [...fromTags, ...fromOther];
};

const refundPolicyLabel = (data) => {
  const type = data.refund_policy_type || "standard_24_hours";
  if (type === "custom") {
    const value = data.custom_refund_policy_value || "—";
    const unit = data.custom_refund_policy_unit || "Hour(s)";
    return `Customer can cancel ${value} ${unit} before the activity start time.`;
  }
  return REFUND_POLICY_OPTIONS.find((option) => option.value === type)?.label || "—";
};

const Step5Review = ({ data, onJumpTo, onChange }) => {
  usePartnerTranslation();
  const inclusions = expandInclusions(data.inclusions_tags, data.inclusions_other);
  const notIncluded = expandInclusions(data.not_included_tags, data.not_included_other);

  return (
    <section data-testid="step-review" className="space-y-6">
      <SectionHeader
        title={tr("Review & publish")}
        subtitle={tr("Make sure everything's filled in before you go live.")}
      />

      {/* ---------- Read-only summary ---------- */}
      <div className="space-y-4">
        <SummaryCard
          title={tr("Basics")}
          onEdit={() => onJumpTo(stepIndex("basics"))}
          testid="summary-basics"
        >
          <Row label={tr("Title")}>{data.title || <em className="text-[#9BA1AB]">{tr("missing")}</em>}</Row>
          <Row label={tr("Short description")}>
            {data.short_description || <em className="text-[#9BA1AB]">{tr("missing")}</em>}
          </Row>
          <Row label={tr("Locations")}>
            {(data.visit_locations || []).join(", ") || <em className="text-[#9BA1AB]">{tr("none")}</em>}
          </Row>
          <Row label={tr("Leader")}>
            {data.experience_leader || <em className="text-[#9BA1AB]">{tr("missing")}</em>}
          </Row>
          <div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#9BA1AB]">{tr("Highlights")}</p>
            <ListLines items={data.highlights?.filter((h) => (h || "").trim())} />
          </div>
          <div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#9BA1AB]">{tr("What's included")}</p>
            <ListLines items={inclusions} />
          </div>
          <div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#9BA1AB]">{tr("What's not included")}</p>
            <ListLines items={notIncluded} />
          </div>
          {(data.suitability_tags || []).length > 0 && (
            <Row label={tr("Suitability")}>
              {(data.suitability_tags || []).join(", ")}
            </Row>
          )}
          {(data.not_suitable_for || []).length > 0 && (
            <Row label={tr("Not suitable for")}>
              {(data.not_suitable_for || []).join(", ")}
            </Row>
          )}
          {data.guest_must_know && (
            <Row label={tr("Guests must know")}>{data.guest_must_know}</Row>
          )}
          <Row label={tr("Refund policy")}>{refundPolicyLabel(data)}</Row>
        </SummaryCard>

        <SummaryCard
          title={tr("Visuals")}
          onEdit={() => onJumpTo(stepIndex("visuals"))}
          testid="summary-visuals"
        >
          {(data.photos || []).length > 0 ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {(data.photos || []).map((p, i) => (
                <div
                  key={i}
                  data-testid={`summary-photo-${i}`}
                  className="aspect-[16/9] overflow-hidden rounded-md border border-[#EAECEF]"
                >
                  <ResponsiveImage imageWidth={960}
                    src={p.url}
                    alt={tr("Photo {{value0}}", { value0: i + 1 })}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <em className="text-[#9BA1AB]">{tr("No photos added yet.")}</em>
          )}
        </SummaryCard>

        <SummaryCard
          title={tr("Options & pricing")}
          onEdit={() => onJumpTo(stepIndex("option"))}
          testid="summary-options"
        >
          {(data.options || []).map((opt, idx) => (
            <div
              key={idx}
              data-testid={`summary-option-${idx}`}
              className="rounded-md border border-[#EAECEF] bg-[#FAFAFB] p-3"
            >
              <p className="text-sm font-bold text-[#0F1115]">
                {opt.name || tr("Option {{value0}}", { value0: idx + 1 })}
              </p>
              <div className="mt-1 grid grid-cols-1 gap-1.5 text-xs text-[#6B7280] sm:grid-cols-2">
                <p>{tr("Pricing:")}{" "}
                  {opt.pricing_basis === "per_group"
                    ? tr("Per group / vehicle — group sizes: {{count}}", { count: (opt.group_sizes || []).filter((s) => Number(s.to) > 0).length })
                    : opt.pricing_basis === "per_person"
                      ? opt.pricing_mode === "flat"
                        ? tr("Per person · same for everyone")
                        : opt.pricing_mode === "per_category"
                          ? tr("Per person · depends on category")
                          : tr("Per person")
                      : "—"}
                </p>
                <p>{tr("Time slots: {{count}}", { count: (opt.schedule_slots || []).length })}
                </p>
                <p>{tr("Languages: ")}{(opt.languages || []).join(", ") || "—"}</p>
                <p>{tr("Type: ")}{opt.privacy_type || "—"}</p>
                <p>
                  {opt.duration_or_validity === "validity"
                    ? tr("Validity: ")
                    : tr("Duration: ")}
                  {(() => {
                    if (opt.duration_or_validity === "validity") {
                      if (opt.validity_type === "day_booked")
                        return tr("Valid on the day booked");
                      if (
                        (opt.validity_type === "from_booking" ||
                          opt.validity_type === "from_activation") &&
                        opt.duration_value
                      ) {
                        const scope =
                          opt.validity_type === "from_booking"
                            ? "from date booked"
                            : "from first activation";
                        return `${opt.duration_value} ${opt.duration_unit?.toLowerCase()} ${scope}`;
                      }
                      return "—";
                    }
                    return opt.duration_value
                      ? `${opt.duration_value} ${opt.duration_unit?.toLowerCase()}`
                      : "—";
                  })()}
                </p>
                <p>{tr("Meeting:")}{" "}
                  {opt.meeting_point_type === "set_meeting_point"
                    ? tr("Set meeting point")
                    : opt.meeting_point_type === "pickup"
                      ? tr("Customer pickup")
                      : "—"}
                </p>
              </div>
            </div>
          ))}
        </SummaryCard>
      </div>

      {/* ---------- Compliance certification ---------- */}
      <div
        data-testid="compliance-cert-block"
        className="rounded-lg border border-[#F0F2F5] bg-white p-3.5"
      >
        <CheckboxField
          value={data.compliance_certified}
          onChange={(v) => onChange("compliance_certified", v)}
          testid="compliance-cert"
          label={
            <span className="block text-sm leading-relaxed text-[#0F1115]">{tr("I certify that all services offered through this listing comply with the applicable laws, licensing, safety, and tax regulations in the jurisdiction(s) where they are provided.")}</span>
          }
        />
      </div>
    </section>
  );
};

export const computeQuality = (data) => {
  let total = 0;
  let done = 0;
  Object.values(RULES).forEach((rules) =>
    rules.forEach((r) => {
      total += 1;
      if (r.check(data)) done += 1;
    }),
  );
  return total === 0 ? 0 : Math.round((done / total) * 100);
};

export const isPublishReady = (data) =>
  Object.values(RULES).every((rules) =>
    rules.every((r) => r.check(data)),
  ) && !!data.compliance_certified;

// Labels of the publish requirements that still fail, so the partner knows what to fix.
export const missingPublishItems = (data) =>
  Object.values(RULES).flat().filter((r) => !r.check(data)).map((r) => r.label);

export default Step5Review;
